/**
 * FRONTEND-EXPERIENCE-ARCHITECTURE-AND-NAVIGATION-CANON-V0
 * Purpose-centered primary navigation — machine-readable SSOT.
 *
 * Runtime menus must remount domainRouteConfig / deriveDomainNavigation output
 * under these areas. Do not invent a second hardcoded path tree.
 */

export const FRONTEND_EXPERIENCE_ARCHITECTURE_AND_NAVIGATION_CANON_PROGRAM_ID_V0 =
  'FRONTEND-EXPERIENCE-ARCHITECTURE-AND-NAVIGATION-CANON-V0' as const;

export type ExperienceAreaId =
  | 'today'
  | 'workbench'
  | 'operations'
  | 'objects'
  | 'evidence_decisions'
  | 'people_authority'
  | 'automation'
  | 'configure'
  | 'operations_console';

export type MobileVisibility = 'primary' | 'secondary' | 'hidden' | 'field_only';

export interface PrimaryNavigationAreaCanon {
  areaId: ExperienceAreaId;
  navKey: string;
  userFacingLabel: string;
  userFacingLabelEn: string;
  purpose: string;
  requiredRoles: readonly string[];
  allowedPlanes: readonly ('business' | 'platform' | 'public' | 'relationship')[];
  mobileVisibility: MobileVisibility;
  defaultLanding: string;
  routeGroups: readonly string[];
  advancedLayer: boolean;
  internalOnly: boolean;
  responsibility: string;
}

/** Exactly seven purpose-centered primary areas (Hick ≤7). */
export const PRIMARY_NAVIGATION_AREAS_V0: readonly PrimaryNavigationAreaCanon[] = [
  {
    areaId: 'today',
    navKey: 'nav-area-today',
    userFacingLabel: '오늘',
    userFacingLabelEn: 'Today',
    purpose: 'Role-aware multi-execution triage — what needs attention now.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'primary',
    defaultLanding: '/workspace',
    routeGroups: ['workspace', 'physical-access'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'triage_only_no_broad_mutation',
  },
  {
    areaId: 'workbench',
    navKey: 'nav-area-workbench',
    userFacingLabel: '워크벤치',
    userFacingLabelEn: 'Workbench',
    purpose: 'One governed execution — request→intent→operation→authority→evidence→result.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'primary',
    defaultLanding: '/workspace/tasks',
    routeGroups: ['workspace-tasks', 'workspace-operations'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'one_governed_execution',
  },
  {
    areaId: 'operations',
    navKey: 'nav-area-operations',
    userFacingLabel: '업무',
    userFacingLabelEn: 'Operations',
    purpose: 'Operation browsing and management with shared Operation grammar.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'secondary',
    defaultLanding: '/flow',
    routeGroups: ['flow', 'warehouse', 'manufacturing', 'finance', 'inventory', 'log'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'operation_browse_and_manage',
  },
  {
    areaId: 'objects',
    navKey: 'nav-area-objects',
    userFacingLabel: '객체',
    userFacingLabelEn: 'Objects',
    purpose: 'Shared operational object context — not master-data tables only.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'secondary',
    defaultLanding: '/item/dashboard',
    routeGroups: ['item', 'inventory', 'space', 'knowledge', 'calendar', 'appeal'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'operational_object_context',
  },
  {
    areaId: 'evidence_decisions',
    navKey: 'nav-area-evidence-decisions',
    userFacingLabel: '증빙·결정',
    userFacingLabelEn: 'Evidence & Decisions',
    purpose:
      'Unified review entry — submitted/reviewed/accepted/approved/executed/reconciled stay distinct.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'primary',
    defaultLanding: '/approvals/pending',
    routeGroups: ['approvals', 'document-review'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'review_and_decision_work',
  },
  {
    areaId: 'people_authority',
    navKey: 'nav-area-people-authority',
    userFacingLabel: '사람·권한',
    userFacingLabelEn: 'People & Authority',
    purpose: 'Actor, capability, delegation and acting-for understanding.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'secondary',
    defaultLanding: '/entity/internal',
    routeGroups: ['entity', 'settings-authority'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'actor_capability_delegation',
  },
  {
    areaId: 'automation',
    navKey: 'nav-area-automation',
    userFacingLabel: '자동화',
    userFacingLabelEn: 'Automation',
    purpose: 'Scheduled, repeated and AI-assisted work entry.',
    requiredRoles: [],
    allowedPlanes: ['business'],
    mobileVisibility: 'secondary',
    defaultLanding: '/automation',
    routeGroups: ['automation'],
    advancedLayer: false,
    internalOnly: false,
    responsibility: 'scheduled_repeated_ai_assisted',
  },
] as const;

/** Role-gated advanced layers — not primary Hick-7 areas. */
export const ADVANCED_NAVIGATION_LAYERS_V0: readonly PrimaryNavigationAreaCanon[] = [
  {
    areaId: 'configure',
    navKey: 'nav-layer-configure',
    userFacingLabel: '구성',
    userFacingLabelEn: 'Configure',
    purpose: 'Advanced tenant structure and configuration.',
    requiredRoles: ['tenant_admin', 'system_operator'],
    allowedPlanes: ['business'],
    mobileVisibility: 'hidden',
    defaultLanding: '/system/dashboard',
    routeGroups: ['system', 'settings', 'finance-advanced', 'export'],
    advancedLayer: true,
    internalOnly: false,
    responsibility: 'tenant_structure_configuration',
  },
  {
    areaId: 'operations_console',
    navKey: 'nav-layer-operations-console',
    userFacingLabel: '운영 콘솔',
    userFacingLabelEn: 'Operations Console',
    purpose: 'System runtime, failure, receipt and recovery.',
    requiredRoles: ['system_operator', 'tenant_admin'],
    allowedPlanes: ['business'],
    mobileVisibility: 'hidden',
    defaultLanding: '/conscious',
    routeGroups: ['conscious', 'pilot-operator-support'],
    advancedLayer: true,
    internalOnly: false,
    responsibility: 'runtime_failure_receipt_recovery',
  },
] as const;

export const ALL_NAVIGATION_AREAS_V0: readonly PrimaryNavigationAreaCanon[] = [
  ...PRIMARY_NAVIGATION_AREAS_V0,
  ...ADVANCED_NAVIGATION_LAYERS_V0,
];

export function getNavigationAreaCanon(areaId: ExperienceAreaId): PrimaryNavigationAreaCanon {
  const found = ALL_NAVIGATION_AREAS_V0.find((area) => area.areaId === areaId);
  if (!found) {
    throw new Error(`Unknown experience area: ${areaId}`);
  }
  return found;
}

export function listPrimaryNavKeysV0(): readonly string[] {
  return PRIMARY_NAVIGATION_AREAS_V0.map((area) => area.navKey);
}

export function assertSevenPrimaryAreasV0(): boolean {
  return PRIMARY_NAVIGATION_AREAS_V0.length === 7;
}
